import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import styled from 'styled-components';
import { getEnv } from 'helper/window-helper';
import { UseAlert } from 'hook/alert-hook';
import { BsCheckLg, BsSearch, BsXLg } from 'react-icons/bs';
import { ItemPedidoDetalhe, PedidoDetalhe, SolicitacaoItemResumo, LogAuditoriaPedido } from '../../../@types/entity/pedido-detalhe-entity';
import { FluxoAprovacaoPedidoStatus } from '../../../@types/enums/fluxo-aprovacao-pedido-status-enum';
import { ItemBusca, SolicitacaoItem } from 'service/api/rest/fluxo-aprovacao-pedido-usecase';
import {
  getAcaoLabel,
  getCodigoItem,
  getStatusBadge,
  getStatusLabel,
  humanizarValorLog,
  resolveAcoesPedido,
  construirSolicitacoes,
  AcaoItemEdicao,
  AdicaoItemEdicao,
  LinhaEdicaoItem,
  formatarCombinacao,
  carregarFiliaisParaCache,
  obterNomeFilialCache,
  SOLICITACAO_STATUS_LABEL,
  SOLICITACAO_TIPO_LABEL,
  MENSAGEM_BLOQUEIO_GERACAO,
  MENSAGEM_BLOQUEIO_PENDENCIA,
  MENSAGEM_EDITAR_PENDENCIA,
  MENSAGEM_ENVIAR_PENDENCIA_CONFIGURACAO,
  exibirColunasCampanha,
} from 'helper/fluxo-aprovacao-pedido-helper';
import { ParseToCurrencyBR } from 'helper/format-currency';
import { TableHeaderStyled } from 'pages/style/table/table-header';
import { ItemTypeahead } from './components/item-typeahead';

/** Limite de altura da lista de itens antes de habilitar o scroll (mesmo modelo da modal legada). */
const ALTURA_MAX_ITENS = '50vh';

/** Mantém a coluna Ações visível no scroll horizontal, com largura para aceitar e recusar lado a lado. */
const GradeSolicitacoesStyled = styled(TableHeaderStyled)`
  th.acoes-solicitacao-col,
  td.acoes-solicitacao-col {
    position: -webkit-sticky;
    position: sticky;
    right: 0;
    white-space: nowrap;
    min-width: 6.5rem;
    width: 6.5rem;
    text-align: right;
    box-shadow: -4px 0 6px -4px rgba(0, 0, 0, 0.18);
  }

  th.acoes-solicitacao-col {
    top: 0;
    z-index: 3;
    background: #f0f0f0;
  }

  td.acoes-solicitacao-col {
    z-index: 1;
    background: #fff;
  }

  .table-striped > tbody > tr:nth-of-type(odd) > td.acoes-solicitacao-col {
    background-color: #f2f2f2;
  }

  td.acoes-solicitacao-col .acoes-solicitacao-botoes {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.35rem;
  }
`;

/** Largura fixa da coluna Nova qtd.; a coluna Ação fica imediatamente à esquerda dela. */
const LARGURA_NOVA_QTD = '8.5rem';
const LARGURA_ACAO_EDICAO = '14rem';

/** Mantém Ação e Nova qtd. visíveis no scroll horizontal, alinhadas à direita. */
const GradeEdicaoStyled = styled(TableHeaderStyled)`
  th.acao-edicao-col,
  td.acao-edicao-col,
  th.nova-qtd-edicao-col,
  td.nova-qtd-edicao-col {
    position: -webkit-sticky;
    position: sticky;
    text-align: right;
    white-space: nowrap;
    background: #fff;
  }

  th.acao-edicao-col,
  td.acao-edicao-col {
    right: ${LARGURA_NOVA_QTD};
    min-width: ${LARGURA_ACAO_EDICAO};
    width: ${LARGURA_ACAO_EDICAO};
    box-shadow: -4px 0 6px -4px rgba(0, 0, 0, 0.18);
  }

  th.nova-qtd-edicao-col,
  td.nova-qtd-edicao-col {
    right: 0;
    min-width: ${LARGURA_NOVA_QTD};
    width: ${LARGURA_NOVA_QTD};
  }

  th.acao-edicao-col,
  th.nova-qtd-edicao-col {
    top: 0;
    z-index: 3;
    background: #f0f0f0;
  }

  td.acao-edicao-col,
  td.nova-qtd-edicao-col {
    z-index: 1;
  }

  .table-striped > tbody > tr:nth-of-type(odd) > td.acao-edicao-col,
  .table-striped > tbody > tr:nth-of-type(odd) > td.nova-qtd-edicao-col {
    background-color: #f2f2f2;
  }

  .acao-edicao-col .form-select,
  .nova-qtd-edicao-col .form-control {
    width: 100%;
    text-align: right;
  }

  tbody tr.item-editado > td,
  .table-striped > tbody > tr.item-editado > td,
  .table-striped > tbody > tr.item-editado:nth-of-type(odd) > td.acao-edicao-col,
  .table-striped > tbody > tr.item-editado:nth-of-type(odd) > td.nova-qtd-edicao-col {
    --bs-table-accent-bg: #fff3cd;
    background-color: #fff3cd;
    box-shadow: inset 0 0 0 9999px #fff3cd;
  }
`;

type Props = {
  detalhe: PedidoDetalhe;
  /** Nome do pedido (origem: PedidoResumo.nome). Exibido no header após o número, quando presente. */
  nome?: string;
  disabled?: boolean;
  onEnviar: (idPedido: number) => void;
  onAprovar: (idPedido: number) => void;
  onValidar: (idPedido: number) => void;
  onReprovar: (idPedido: number, justificativa: string) => void;
  /** Acao "Editar" (§4.5): abre solicitacoes item-a-item, nao substitui a versao. */
  onEditar: (idPedido: number, solicitacoes: SolicitacaoItem[], justificativa: string) => void;
  onGerar: (idPedido: number) => void;
  /** Decide as solicitacoes pendentes (aceitar/recusar em lote). */
  onDecidir?: (idPedido: number, idsAceitas: number[], idsRecusadas: number[]) => void;
  /** Retira uma solicitacao pendente da propria autoria (RN18). */
  onRetirar?: (idPedido: number, idSolicitacao: number) => void;
  onVoltar?: () => void;
  /** Busca (typeahead) de itens disponíveis da filial do pedido no modo edição. */
  onBuscarItens?: (termo: string) => Promise<ItemBusca[]>;
};

/** Abertura de chamados (GLPI) para solicitar a configuração de aprovador da loja. */
const URL_ABERTURA_CHAMADO_GLPI = 'https://atendimento.lumegrupo.com.br/front/ticket.form.php';

type Modo = 'visualizar' | 'editar' | 'reprovar';

/** Colunas de análise compartilhadas pela visualização, pela edição e pela lista de itens adicionados. */
type CamposExibicao = {
  qme?: number;
  precoReposicao?: number;
  custoMedio?: number;
  precoPedido?: number;
  cvf?: string;
  ncvf?: string;
  pontuacao?: number;
  mediaDemanda?: number;
  demandaMesAtual?: number;
  demandaMes1?: number;
  demandaMes2?: number;
  demandaMes3?: number;
  demandaMes4?: number;
  demandaMes5?: number;
  estoque?: number;
  bloqueadas?: number;
  pendentes?: number;
  precoSemSt?: number;
  precoSemStDesconto?: number;
  precoComSt?: number;
  totalizadorPrecoSemSt?: number;
  totalizadorPrecoSemStDesconto?: number;
  totalizadorPrecoComSt?: number;
};

/** Linha da grade de edição: ação escolhida mais as mesmas colunas da visualização. */
type LinhaEdicao = LinhaEdicaoItem & CamposExibicao;

/** Adição em composição no modo de edição (idItem + descrição/valor do typeahead + quantidade). */
type Adicao = AdicaoItemEdicao & { descricao?: string } & CamposExibicao;

/**
 * Tag de status com a paleta central (`getStatusBadge`), a mesma da listagem.
 * O `text` vem do helper porque o badge do Bootstrap usa texto branco por padrão, ilegível sobre
 * os fundos claros (âmbar/ciano).
 */
function renderBadgeStatus(status: FluxoAprovacaoPedidoStatus) {
  const estilo = getStatusBadge(status);
  return (
    <Badge bg={estilo.bg} text={estilo.text} data-testid="status-pedido">
      {getStatusLabel(status)}
    </Badge>
  );
}

/** Formata o valor do item como moeda BR (R$), mantendo o fallback "-" quando ausente. */
function formatarValor(valor?: number): string {
  if (valor === undefined || valor === null) {
    return '-';
  }
  return ParseToCurrencyBR(valor);
}

function camposExibicao(origem: CamposExibicao): CamposExibicao {
  return {
    qme: origem.qme,
    precoReposicao: origem.precoReposicao,
    custoMedio: origem.custoMedio,
    precoPedido: origem.precoPedido,
    cvf: origem.cvf,
    ncvf: origem.ncvf,
    pontuacao: origem.pontuacao,
    mediaDemanda: origem.mediaDemanda,
    demandaMesAtual: origem.demandaMesAtual,
    demandaMes1: origem.demandaMes1,
    demandaMes2: origem.demandaMes2,
    demandaMes3: origem.demandaMes3,
    demandaMes4: origem.demandaMes4,
    demandaMes5: origem.demandaMes5,
    estoque: origem.estoque,
    bloqueadas: origem.bloqueadas,
    pendentes: origem.pendentes,
    precoSemSt: origem.precoSemSt,
    precoSemStDesconto: origem.precoSemStDesconto,
    precoComSt: origem.precoComSt,
    totalizadorPrecoSemSt: origem.totalizadorPrecoSemSt,
    totalizadorPrecoSemStDesconto: origem.totalizadorPrecoSemStDesconto,
    totalizadorPrecoComSt: origem.totalizadorPrecoComSt,
  };
}

function itemDaLinha(linha: LinhaEdicao): ItemColunasListagem {
  return {
    idItem: linha.idItem,
    codigo: linha.codigo,
    descricao: linha.descricao,
    quantidade: linha.quantidadeAtual,
    ...camposExibicao(linha),
  };
}

/** Total da linha de campanha: quantidade × QME × preço. Ausente quando falta algum fator. */
function totalCampanha(quantidade?: number, qme?: number, preco?: number): number | undefined {
  if (quantidade == null || qme == null || preco == null) {
    return undefined;
  }
  return quantidade * qme * preco;
}

function totalOuInformado(quantidade?: number, qme?: number, preco?: number, informado?: number): number | undefined {
  const calculado = totalCampanha(quantidade, qme, preco);
  return calculado === undefined ? informado : calculado;
}

function itemDaAdicao(adicao: Adicao): ItemColunasListagem {
  const campos = camposExibicao(adicao);
  return {
    idItem: adicao.idItem,
    codigo: adicao.codigo,
    descricao: adicao.descricao,
    quantidade: adicao.quantidade,
    ...campos,
    totalizadorPrecoSemSt: totalOuInformado(adicao.quantidade, adicao.qme, adicao.precoSemSt, campos.totalizadorPrecoSemSt),
    totalizadorPrecoSemStDesconto: totalOuInformado(
      adicao.quantidade,
      adicao.qme,
      adicao.precoSemStDesconto,
      campos.totalizadorPrecoSemStDesconto
    ),
    totalizadorPrecoComSt: totalOuInformado(adicao.quantidade, adicao.qme, adicao.precoComSt, campos.totalizadorPrecoComSt),
  };
}

function textoBusca(valor?: string): string {
  return (valor ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLocaleLowerCase('pt-BR');
}

function linhaCombinaBusca(linha: { codigo?: string; idItem: number; descricao?: string }, termo: string): boolean {
  const busca = textoBusca(termo);
  if (busca === '') {
    return true;
  }
  const sufixo = textoBusca(getCodigoItem(linha));
  const descricao = textoBusca(linha.descricao);
  return sufixo.includes(busca) || descricao.includes(busca);
}

/** Solicitações da rodada mais recente: o bloco final enviado pela mesma parte (lista vem em ordem de criação). */
function solicitacoesDaUltimaRodada(solicitacoes: SolicitacaoItemResumo[]): SolicitacaoItemResumo[] {
  if (solicitacoes.length === 0) {
    return [];
  }
  const parteAutor = solicitacoes[solicitacoes.length - 1].parteAutor;
  let inicio = solicitacoes.length - 1;
  while (inicio > 0 && solicitacoes[inicio - 1].parteAutor === parteAutor) {
    inicio--;
  }
  return solicitacoes.slice(inicio);
}

/** Itens alterados e aceitos na última rodada de aprovação; aparecem no topo da edição, em destaque. */
function itensAlteradosAceitos(solicitacoes?: SolicitacaoItemResumo[]): Set<number> {
  return new Set(
    solicitacoesDaUltimaRodada(solicitacoes ?? [])
      .filter((s) => s.status === 'ACEITA')
      .map((s) => s.idItem)
  );
}

function linhasFiltradas(
  linhas: LinhaEdicao[],
  termo: string,
  alterados: Set<number>
): Array<{ linha: LinhaEdicao; indice: number }> {
  const encontradas = linhas
    .map((linha, indice) => ({ linha, indice }))
    .filter((entrada) => linhaCombinaBusca(entrada.linha, termo));
  return [...encontradas].sort(
    (a, b) => Number(alterados.has(b.linha.idItem)) - Number(alterados.has(a.linha.idItem))
  );
}

function linhasIniciais(itens: ItemPedidoDetalhe[]): LinhaEdicao[] {
  return itens.map((item) => ({
    idItem: item.idItem,
    codigo: item.codigo,
    descricao: item.descricao,
    quantidadeAtual: item.quantidade,
    acao: 'MANTER' as AcaoItemEdicao,
    quantidadeNova: item.quantidade,
    ...camposExibicao(item),
  }));
}

export function FluxoAprovacaoPedidoOperadorPage({
  detalhe,
  nome,
  disabled = false,
  onEnviar,
  onAprovar,
  onValidar,
  onReprovar,
  onEditar,
  onGerar,
  onDecidir,
  onRetirar,
  onVoltar,
  onBuscarItens,
}: Props) {
  const { showAlert } = UseAlert();
  const [modo, setModo] = useState<Modo>('visualizar');
  const [linhas, setLinhas] = useState<LinhaEdicao[]>([]);
  const [adicoes, setAdicoes] = useState<Adicao[]>([]);
  const [novaAdicao, setNovaAdicao] = useState<Adicao>({ idItem: 0, quantidade: 1 });
  const [justificativa, setJustificativa] = useState('');
  const [itensExpandidos, setItensExpandidos] = useState<boolean>((detalhe.solicitacoes ?? []).length === 0);
  const [buscaItensAberta, setBuscaItensAberta] = useState(false);
  const [termoBuscaItens, setTermoBuscaItens] = useState('');

  const acoes = resolveAcoesPedido(detalhe);
  const id = detalhe.idPedido;
  const solicitacoes = detalhe.solicitacoes ?? [];
  const solicitacoesCompostas = construirSolicitacoes(linhas, adicoes);
  const idsDecidiveis = solicitacoes.filter((s) => s.status === 'PENDENTE' && s.podeDecidir).map((s) => s.id);
  const [selecionadas, setSelecionadas] = useState<number[]>([]);

  // Descarta da seleção o que deixou de ser decidível (ex.: após decidir ou recarregar o detalhe).
  useEffect(() => {
    setSelecionadas((atuais) => atuais.filter((idSel) => idsDecidiveis.includes(idSel)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalhe.solicitacoes]);
  const edicaoInvalida = justificativa.trim() === '' || solicitacoesCompostas.length === 0;
  const motivoEdicaoBloqueada = resolverMotivoEdicaoBloqueada(justificativa, solicitacoesCompostas.length);

  const [nomeFilialResolvido, setNomeFilialResolvido] = useState<string | undefined>(detalhe.nomeFilial);

  useEffect(() => {
    setNomeFilialResolvido(detalhe.nomeFilial || obterNomeFilialCache(detalhe.idFilial));
    if (!detalhe.nomeFilial && detalhe.idFilial) {
      carregarFiliaisParaCache().then((mapa) => {
        const nome = mapa.get(Number(detalhe.idFilial));
        if (nome) {
          setNomeFilialResolvido(nome);
        }
      });
    }
  }, [detalhe.idFilial, detalhe.nomeFilial]);

  useEffect(() => {
    setModo('visualizar');
  }, [detalhe.versaoAtual]);

  function iniciarEdicao() {
    setLinhas(linhasIniciais(detalhe.itens));
    setAdicoes([]);
    setNovaAdicao({ idItem: 0, quantidade: 1 });
    setJustificativa('');
    setBuscaItensAberta(false);
    setTermoBuscaItens('');
    setModo('editar');
  }

  function iniciarReprovacao() {
    setJustificativa('');
    setModo('reprovar');
  }

  function cancelar() {
    setModo('visualizar');
    setJustificativa('');
    setLinhas([]);
    setAdicoes([]);
    setBuscaItensAberta(false);
    setTermoBuscaItens('');
  }

  function alterarAcao(indice: number, acao: AcaoItemEdicao) {
    setLinhas((atual) => atual.map((linha, i) => (i === indice ? { ...linha, acao } : linha)));
  }

  function alterarQuantidade(indice: number, quantidadeNova: number) {
    setLinhas((atual) => atual.map((linha, i) => (i === indice ? { ...linha, quantidadeNova } : linha)));
  }

  function selecionarNovoItem(item: ItemBusca) {
    const quantidadeSugerida = item.quantidadeSugerida && item.quantidadeSugerida > 0 ? item.quantidadeSugerida : 1;
    setNovaAdicao({
      idItem: item.idItem,
      codigo: item.codigo,
      descricao: item.descricao,
      valor: item.valor,
      quantidade: quantidadeSugerida,
      ...camposExibicao(item),
    });
  }

  function adicionarItem() {
    if (novaAdicao.idItem <= 0 || novaAdicao.quantidade <= 0) {
      return;
    }
    if (linhas.some((linha) => linha.idItem === novaAdicao.idItem)) {
      avisar('Este item já pertence ao pedido. Para alterar a quantidade, use a listagem acima.');
      return;
    }
    if (adicoes.some((adicao) => adicao.idItem === novaAdicao.idItem)) {
      avisar('Este item já foi adicionado nesta edição.');
      return;
    }
    setAdicoes((atual) => [...atual, novaAdicao]);
    setNovaAdicao({ idItem: 0, quantidade: 1 });
  }

  function removerAdicao(indice: number) {
    setAdicoes((atual) => atual.filter((_, i) => i !== indice));
  }

  function confirmarEdicao() {
    onEditar(id, solicitacoesCompostas, justificativa);
  }

  /** Decide UMA solicitação (aceitar/recusar) diretamente na linha. */
  function decidirUma(idSolicitacao: number, aceitar: boolean) {
    if (!onDecidir) {
      return;
    }
    onDecidir(id, aceitar ? [idSolicitacao] : [], aceitar ? [] : [idSolicitacao]);
  }

  function alternarSelecao(idSolicitacao: number) {
    setSelecionadas((atuais) =>
      atuais.includes(idSolicitacao) ? atuais.filter((idSel) => idSel !== idSolicitacao) : [...atuais, idSolicitacao]
    );
  }

  function alternarTodas(marcar: boolean) {
    setSelecionadas(marcar ? idsDecidiveis : []);
  }

  /** Aceita ou recusa, em massa, as solicitações selecionadas. */
  function decidirSelecionadas(aceitar: boolean) {
    if (!onDecidir || selecionadas.length === 0) {
      return;
    }
    onDecidir(id, aceitar ? selecionadas : [], aceitar ? [] : selecionadas);
    setSelecionadas([]);
  }

  function avisar(mensagem: string) {
    showAlert?.({ show: true, content: mensagem, color: 'warning', time: 5000 });
  }

  function renderAcoes() {
    const mensagemEnvioBloqueado = detalhe.pendenciaAprovadorVinculado
      ? MENSAGEM_ENVIAR_PENDENCIA_CONFIGURACAO
      : MENSAGEM_BLOQUEIO_PENDENCIA;
    return (
      <div className="mt-3 d-flex justify-content-end">
        {onVoltar && (
          <Button variant="outline-primary" className="mx-1" disabled={disabled} onClick={onVoltar}>
            Voltar à lista
          </Button>
        )}
        {acoes.mostrarAprovar && (
          <Button variant="success" className="mx-1" disabled={disabled} onClick={() => onAprovar(id)}>
            Aprovar
          </Button>
        )}
        {/* O operador também APROVA o pedido — o rótulo é "Aprovar" nos dois turnos.
            O identificador/endpoint continua "validar" (aprovação do lado do operador, RF08). */}
        {acoes.mostrarValidar && (
          <Button variant="success" className="mx-1" disabled={disabled} onClick={() => onValidar(id)}>
            Aprovar
          </Button>
        )}
        {acoes.mostrarEditar && (
          <Button
            variant="warning"
            className={`mx-1${acoes.editarBloqueadoPorPendencia ? ' opacity-50' : ''}`}
            disabled={disabled}
            aria-disabled={acoes.editarBloqueadoPorPendencia}
            title={acoes.editarBloqueadoPorPendencia ? MENSAGEM_EDITAR_PENDENCIA : undefined}
            onClick={() => {
              if (acoes.editarBloqueadoPorPendencia) {
                avisar(MENSAGEM_EDITAR_PENDENCIA);
                return;
              }
              iniciarEdicao();
            }}
          >
            Editar
          </Button>
        )}
        {acoes.mostrarEnviar && (
          <Button
            variant="primary"
            className={`mx-1${acoes.enviarBloqueadoPorPendencia ? ' opacity-50' : ''}`}
            disabled={disabled}
            aria-disabled={acoes.enviarBloqueadoPorPendencia}
            title={acoes.enviarBloqueadoPorPendencia ? mensagemEnvioBloqueado : undefined}
            onClick={() => {
              if (acoes.enviarBloqueadoPorPendencia) {
                avisar(mensagemEnvioBloqueado);
                return;
              }
              onEnviar(id);
            }}
          >
            Enviar para aprovação
          </Button>
        )}
        {acoes.mostrarReprovar && (
          <Button variant="outline-danger" className="mx-1" disabled={disabled} onClick={iniciarReprovacao}>
            Reprovar
          </Button>
        )}
        {acoes.mostrarGerar && (
          <Button variant="success" className="mx-1" disabled={disabled} onClick={() => onGerar(id)}>
            Gerar pedido
          </Button>
        )}
      </div>
    );
  }

  function renderMensagens() {
    return (
      <>
        {acoes.bloqueadoPorPendencia && (
          <div className="text-danger mt-3" data-testid="mensagem-bloqueio-pendencia">
            {MENSAGEM_BLOQUEIO_PENDENCIA}
          </div>
        )}
        {acoes.mostrarBloqueio && <div className="text-muted mt-3">{MENSAGEM_BLOQUEIO_GERACAO}</div>}
      </>
    );
  }

  function renderPendenciaConfiguracao() {
    const urlGlpi = getEnv('REACT_APP_GLPI_URL') || URL_ABERTURA_CHAMADO_GLPI;
    return (
      <Alert variant="warning" className="mt-3" data-testid="aviso-pendencia-configuracao">
        <strong>Pendência de configuração:</strong> não há aprovador ativo vinculado à loja deste pedido, por isso ele
        permanece em elaboração. Solicite o vínculo de um aprovador{' '}
        <a href={urlGlpi} target="_blank" rel="noopener noreferrer">
          abrindo um chamado no GLPI
        </a>
        .
      </Alert>
    );
  }

  function renderVisualizar() {
    const temSolicitacoes = solicitacoes.length > 0;
    return (
      <>
        {renderSolicitacoes(solicitacoes, disabled, detalhe.origem, {
          decidirUma,
          selecionadas,
          idsDecidiveis,
          alternarSelecao,
          alternarTodas,
          decidirSelecionadas,
          onRetirar: onRetirar ? (idSolicitacao: number) => onRetirar(id, idSolicitacao) : undefined,
        })}
        {renderJustificativaUltimaAlteracao(detalhe.justificativaUltimaAlteracao)}
        {detalhe.pendenciaAprovadorVinculado && renderPendenciaConfiguracao()}
        {renderCabecalhoItens(temSolicitacoes, itensExpandidos, () => setItensExpandidos((v) => !v))}
        {itensExpandidos && renderItens(detalhe.itens, detalhe.origem)}
        {renderAcoes()}
        {renderMensagens()}
        {renderAuditoria(detalhe.auditoria)}
      </>
    );
  }

  function alternarBuscaItens() {
    setBuscaItensAberta((aberta) => !aberta);
    setTermoBuscaItens('');
  }

  function renderEditar() {
    const emElaboracao = detalhe.status === FluxoAprovacaoPedidoStatus.EM_ELABORACAO;
    const campanha = exibirColunasCampanha(detalhe.origem);
    return (
      <>
        <div className="text-muted mb-2">
          {emElaboracao
            ? 'Pedido em elaboração: salve as alterações para criar uma nova versão antes de enviar para aprovação.'
            : 'A edição abre solicitações item-a-item para a contraparte decidir (não substitui a versão).'}
        </div>
        {renderCabecalhoEdicao(buscaItensAberta, termoBuscaItens, alternarBuscaItens, setTermoBuscaItens)}
        {renderGradeEdicao(linhas, buscaItensAberta ? termoBuscaItens : '', campanha, disabled, {
          alterarAcao,
          alterarQuantidade,
          alterados: itensAlteradosAceitos(detalhe.solicitacoes),
        })}
        {renderAdicao(novaAdicao, adicoes, campanha, disabled, {
          selecionarNovoItem,
          alterarQuantidadeNova: (q: number) => setNovaAdicao((atual) => ({ ...atual, quantidade: q })),
          adicionarItem,
          removerAdicao,
          onBuscarItens,
        })}
        {renderJustificativa(justificativa, setJustificativa, 'Justificativa (obrigatória)')}
        <div className="mt-3 d-flex justify-content-end">
          <Button
            variant="primary"
            className={`mx-1${edicaoInvalida ? ' opacity-50' : ''}`}
            disabled={disabled}
            aria-disabled={edicaoInvalida}
            title={edicaoInvalida ? motivoEdicaoBloqueada : undefined}
            onClick={() => {
              if (edicaoInvalida) {
                avisar(motivoEdicaoBloqueada);
                return;
              }
              confirmarEdicao();
            }}
          >
            {emElaboracao ? 'Salvar nova versão' : 'Enviar solicitações'}
          </Button>
          <Button variant="outline-secondary" className="mx-1" disabled={disabled} onClick={cancelar}>
            Cancelar
          </Button>
        </div>
      </>
    );
  }

  function renderReprovar() {
    return (
      <>
        {renderJustificativa(justificativa, setJustificativa, 'Motivo da reprovação (opcional)')}
        <div className="mt-3 d-flex justify-content-end">
          <Button variant="danger" className="mx-1" disabled={disabled} onClick={() => onReprovar(id, justificativa)}>
            Confirmar reprovação
          </Button>
          <Button variant="outline-secondary" className="mx-1" disabled={disabled} onClick={cancelar}>
            Cancelar
          </Button>
        </div>
      </>
    );
  }

  return (
    <Card>
      <Card.Body>
        <Card.Title>
          Pedido de Compra{id ? ` nº ${id}` : ''}
          {nome && nome.trim() !== '' ? ` — ${nome}` : ''}
        </Card.Title>

        <div className="mb-3">Status: {renderBadgeStatus(detalhe.status)}</div>

        {formatarCombinacao(detalhe, nomeFilialResolvido) !== '' && (
          <div className="text-muted mb-3" data-testid="combinacao-pedido">
            {formatarCombinacao(detalhe, nomeFilialResolvido)}
          </div>
        )}

        {modo === 'visualizar' && renderVisualizar()}
        {modo === 'editar' && renderEditar()}
        {modo === 'reprovar' && renderReprovar()}
      </Card.Body>
    </Card>
  );
}

/** Campos compartilhados entre Itens do pedido e Solicitações de alteração. */
type ItemColunasListagem = CamposExibicao & {
  idItem: number;
  codigo?: string;
  descricao?: string;
  quantidade?: number;
  valor?: number;
};

function cabecalhosColunasItem(campanha: boolean) {
  return (
    <>
      <th className="text-start">Descrição</th>
      <th className="text-center">Código Fabricante</th>
      <th className="text-end">Qtd</th>
      <th className="text-end">Qme</th>
      <th className="text-end">Preço reposição</th>
      <th className="text-end">Custo Médio</th>
      <th className="text-end">Preço pedido</th>
      <th className="text-center">Cvf</th>
      <th className="text-center">Ncvf</th>
      <th className="text-center">Pontuação</th>
      <th className="text-end">Média demanda</th>
      <th className="text-end">Mês Atual</th>
      <th className="text-end">Demandas M-1</th>
      <th className="text-end">Demandas M-2</th>
      <th className="text-end">Demandas M-3</th>
      <th className="text-end">Demandas M-4</th>
      <th className="text-end">Demandas M-5</th>
      <th className="text-end">Estoque</th>
      <th className="text-end">Bloqueadas</th>
      <th className="text-end">Pendentes</th>
      {campanha && <th>Preco sem ST</th>}
      {campanha && <th>Preco Sem ST com Desconto</th>}
      {campanha && <th>Preco com ST</th>}
      {campanha && <th>Totalizador preco sem ST</th>}
      {campanha && <th>Totalizador preco sem ST com desconto</th>}
      {campanha && <th>Totalizador preco com ST</th>}
    </>
  );
}

function celulasColunasItem(item: ItemColunasListagem, campanha: boolean) {
  return (
    <>
      <td className="text-start">{item.descricao ?? '-'}</td>
      <td className="text-center">{getCodigoItem(item)}</td>
      <td className="text-end">{item.quantidade ?? '-'}</td>
      <td className="text-end">{item.qme ?? '-'}</td>
      <td className="text-end">{formatarValor(item.precoReposicao)}</td>
      <td className="text-end">{formatarValor(item.custoMedio)}</td>
      <td className="text-end">{formatarValor(item.precoPedido)}</td>
      <td className="text-center">{item.cvf ?? '-'}</td>
      <td className="text-center">{item.ncvf ?? '-'}</td>
      <td className="text-center">{item.pontuacao ?? '-'}</td>
      <td className="text-end">
        {item.mediaDemanda !== undefined && item.mediaDemanda !== null ? item.mediaDemanda.toFixed(2) : '-'}
      </td>
      <td className="text-end">{item.demandaMesAtual ?? '-'}</td>
      <td className="text-end">{item.demandaMes1 ?? '-'}</td>
      <td className="text-end">{item.demandaMes2 ?? '-'}</td>
      <td className="text-end">{item.demandaMes3 ?? '-'}</td>
      <td className="text-end">{item.demandaMes4 ?? '-'}</td>
      <td className="text-end">{item.demandaMes5 ?? '-'}</td>
      <td className="text-end">{item.estoque ?? '-'}</td>
      <td className="text-end">{item.bloqueadas ?? '-'}</td>
      <td className="text-end">{item.pendentes ?? '-'}</td>
      {campanha && <td>{formatarValor(item.precoSemSt)}</td>}
      {campanha && <td>{formatarValor(item.precoSemStDesconto)}</td>}
      {campanha && <td>{formatarValor(item.precoComSt)}</td>}
      {campanha && <td>{formatarValor(item.totalizadorPrecoSemSt)}</td>}
      {campanha && <td>{formatarValor(item.totalizadorPrecoSemStDesconto)}</td>}
      {campanha && <td>{formatarValor(item.totalizadorPrecoComSt)}</td>}
    </>
  );
}

function renderItens(itens: ItemPedidoDetalhe[], origem?: string) {
  if (!itens || itens.length === 0) {
    return <div className="text-muted">Nenhum item no pedido.</div>;
  }
  const campanha = exibirColunasCampanha(origem);
  return (
    <TableHeaderStyled style={{ maxHeight: ALTURA_MAX_ITENS }}>
      <Table striped bordered size="sm" data-testid="grade-itens">
        <thead>
          <tr>{cabecalhosColunasItem(campanha)}</tr>
        </thead>
        <tbody>
          {itens.map((item, indice) => (
            <tr key={`${item.idItem}-${indice}`}>{celulasColunasItem(item, campanha)}</tr>
          ))}
        </tbody>
      </Table>
    </TableHeaderStyled>
  );
}

/** Explica por que "Enviar solicitações" está bloqueado (justificativa ausente e/ou nenhuma alteração). */
function resolverMotivoEdicaoBloqueada(justificativa: string, qtdAlteracoes: number): string {
  const faltas: string[] = [];
  if (qtdAlteracoes === 0) {
    faltas.push('altere ao menos um item (ação ou quantidade) ou adicione um item');
  }
  if (justificativa.trim() === '') {
    faltas.push('informe a justificativa (obrigatória)');
  }
  return faltas.length === 0 ? '' : `Não é possível enviar: ${faltas.join(' e ')}.`;
}

type SolicitacoesHandlers = {
  decidirUma: (idSolicitacao: number, aceitar: boolean) => void;
  selecionadas: number[];
  idsDecidiveis: number[];
  alternarSelecao: (idSolicitacao: number) => void;
  alternarTodas: (marcar: boolean) => void;
  decidirSelecionadas: (aceitar: boolean) => void;
  onRetirar?: (idSolicitacao: number) => void;
};

function solicitacaoTemAcao(solicitacao: SolicitacaoItemResumo, podeRetirar: boolean): boolean {
  return Boolean(solicitacao.podeDecidir) || (Boolean(solicitacao.podeRetirar) && podeRetirar);
}

function renderSolicitacoes(
  solicitacoes: SolicitacaoItemResumo[],
  disabled: boolean,
  origem: string | undefined,
  handlers: SolicitacoesHandlers
) {
  if (!solicitacoes || solicitacoes.length === 0) {
    return null;
  }
  const campanha = exibirColunasCampanha(origem);
  const mostrarSelecao = handlers.idsDecidiveis.length > 0;
  const mostrarAcoes = solicitacoes.some((s) => solicitacaoTemAcao(s, Boolean(handlers.onRetirar)));
  const colunasAcao = { selecao: mostrarSelecao, acoes: mostrarAcoes };
  return (
    <div className="mt-2 mb-4" data-testid="secao-solicitacoes">
      <h6>Solicitações de alteração</h6>
      <GradeSolicitacoesStyled style={{ maxHeight: ALTURA_MAX_ITENS }}>
        <Table striped bordered size="sm" data-testid="grade-solicitacoes">
          <thead>
            <tr>
              {mostrarSelecao && (
                <th style={{ width: '32px' }}>
                  <Form.Check
                    type="checkbox"
                    aria-label="selecionar-todas-solicitacoes"
                    disabled={disabled}
                    checked={handlers.selecionadas.length === handlers.idsDecidiveis.length}
                    onChange={(e) => handlers.alternarTodas(e.target.checked)}
                  />
                </th>
              )}
              <th>Tipo</th>
              {cabecalhosColunasItem(campanha)}
              <th>Alteração</th>
              <th>Autor</th>
              <th>Situação</th>
              {mostrarAcoes && <th className="acoes-solicitacao-col">Ações</th>}
            </tr>
          </thead>
          <tbody>{solicitacoes.map((s) => renderLinhaSolicitacao(s, disabled, campanha, handlers, colunasAcao))}</tbody>
        </Table>
      </GradeSolicitacoesStyled>
      {handlers.idsDecidiveis.length > 0 && (
        <div className="d-flex justify-content-end align-items-center mt-2" data-testid="acoes-solicitacoes-lote">
          <span className="text-muted me-2">{handlers.selecionadas.length} selecionada(s)</span>
          <Button
            variant="success"
            size="sm"
            className="mx-1"
            disabled={disabled || handlers.selecionadas.length === 0}
            onClick={() => handlers.decidirSelecionadas(true)}
          >
            Aceitar selecionadas
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="mx-1"
            disabled={disabled || handlers.selecionadas.length === 0}
            onClick={() => handlers.decidirSelecionadas(false)}
          >
            Recusar selecionadas
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Ações por linha: reusa os mesmos ícones do Analítico — check verde (BsCheckLg)
 * para aceitar/salvar e X vermelho (BsXLg) para recusar/excluir — cada linha decide a própria
 * solicitação; a decisão em massa usa os checkboxes da linha e os botões abaixo da grade.
 */
function renderLinhaSolicitacao(
  s: SolicitacaoItemResumo,
  disabled: boolean,
  campanha: boolean,
  handlers: SolicitacoesHandlers,
  colunasAcao: { selecao: boolean; acoes: boolean }
) {
  const itemColunas: ItemColunasListagem = {
    idItem: s.idItem,
    codigo: s.codigo,
    descricao: s.descricao,
    quantidade: s.quantidadeNova ?? s.quantidadeAnterior,
    valor: s.valor,
    qme: s.qme,
    precoReposicao: s.precoReposicao,
    custoMedio: s.custoMedio,
    precoPedido: s.precoPedido ?? s.valor,
    cvf: s.cvf,
    ncvf: s.ncvf,
    pontuacao: s.pontuacao,
    mediaDemanda: s.mediaDemanda,
    demandaMesAtual: s.demandaMesAtual,
    demandaMes1: s.demandaMes1,
    demandaMes2: s.demandaMes2,
    demandaMes3: s.demandaMes3,
    demandaMes4: s.demandaMes4,
    demandaMes5: s.demandaMes5,
    estoque: s.estoque,
    bloqueadas: s.bloqueadas,
    pendentes: s.pendentes,
    precoSemSt: s.precoSemSt,
    precoSemStDesconto: s.precoSemStDesconto,
    precoComSt: s.precoComSt,
    totalizadorPrecoSemSt: s.totalizadorPrecoSemSt,
    totalizadorPrecoSemStDesconto: s.totalizadorPrecoSemStDesconto,
    totalizadorPrecoComSt: s.totalizadorPrecoComSt,
  };
  return (
    <tr key={`sol-${s.id}`}>
      {colunasAcao.selecao && (
        <td>
          {s.podeDecidir && s.status === 'PENDENTE' && (
            <Form.Check
              type="checkbox"
              aria-label={`selecionar-solicitacao-${s.id}`}
              disabled={disabled}
              checked={handlers.selecionadas.includes(s.id)}
              onChange={() => handlers.alternarSelecao(s.id)}
            />
          )}
        </td>
      )}
      <td>{SOLICITACAO_TIPO_LABEL[s.tipo] ?? s.tipo}</td>
      {celulasColunasItem(itemColunas, campanha)}
      <td>{descreverAlteracao(s)}</td>
      <td>{s.parteAutor ?? '-'}</td>
      <td>{SOLICITACAO_STATUS_LABEL[s.status] ?? s.status}</td>
      {colunasAcao.acoes && (
      <td className="acoes-solicitacao-col">
        <div className="acoes-solicitacao-botoes">
          {s.podeDecidir && (
            <>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-success"
                aria-label={`aceitar-solicitacao-${s.id}`}
                disabled={disabled}
                onClick={() => handlers.decidirUma(s.id, true)}
              >
                <BsCheckLg />
              </Button>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-danger"
                aria-label={`recusar-solicitacao-${s.id}`}
                disabled={disabled}
                onClick={() => handlers.decidirUma(s.id, false)}
              >
                <BsXLg />
              </Button>
            </>
          )}
          {s.podeRetirar && handlers.onRetirar && (
            <Button variant="outline-secondary" size="sm" disabled={disabled} onClick={() => handlers.onRetirar!(s.id)}>
              Retirar
            </Button>
          )}
        </div>
      </td>
      )}
    </tr>
  );
}

function renderJustificativaUltimaAlteracao(justificativa?: string) {
  if (!justificativa || justificativa.trim() === '') {
    return null;
  }
  return (
    <div className="mb-3" data-testid="justificativa-ultima-alteracao">
      <strong>Justificativa da última alteração:</strong> {justificativa}
    </div>
  );
}

/**
 * Cabeçalho da listagem de itens: quando há solicitações, mostra o botão de
 * expandir/recolher a listagem (colapsada por padrão). Sem solicitações, apenas o título simples.
 */
function renderCabecalhoItens(temSolicitacoes: boolean, expandido: boolean, alternar: () => void) {
  if (!temSolicitacoes) {
    return <h6 className="mt-2">Itens do pedido</h6>;
  }
  return (
    <div className="d-flex align-items-center mt-2">
      <h6 className="mb-0 me-2">Itens do pedido</h6>
      <Button variant="link" size="sm" className="p-0" aria-label="alternar-itens" onClick={alternar}>
        {expandido ? 'Recolher' : 'Expandir'}
      </Button>
    </div>
  );
}

function descreverAlteracao(s: SolicitacaoItemResumo): string {
  if (s.tipo === 'REMOCAO') {
    return 'Remover item';
  }
  if (s.tipo === 'ADICAO') {
    return `Adicionar ${s.quantidadeNova ?? '-'} × ${formatarValor(s.valor)}`;
  }
  return `Qtd ${s.quantidadeAnterior ?? '-'} → ${s.quantidadeNova ?? '-'}`;
}

type GradeEdicaoHandlers = {
  alterarAcao: (indice: number, acao: AcaoItemEdicao) => void;
  alterarQuantidade: (indice: number, quantidadeNova: number) => void;
  alterados: Set<number>;
};

function renderCabecalhoEdicao(
  aberta: boolean,
  termo: string,
  onAlternar: () => void,
  onTermo: (valor: string) => void
) {
  return (
    <div className="d-flex align-items-center justify-content-between mb-2" data-testid="cabecalho-edicao">
      <h6 className="mb-0">Itens do pedido</h6>
      <div className="d-flex align-items-center">
        {aberta && (
          <Form.Control
            className="me-2"
            style={{ width: 260 }}
            aria-label="filtro-itens-edicao"
            placeholder="Sufixo ou descrição"
            value={termo}
            autoFocus
            onChange={(e) => onTermo(e.target.value)}
          />
        )}
        <Button
          variant="link"
          className="p-0"
          aria-label="buscar-itens-edicao"
          title="Buscar por sufixo ou descrição"
          onClick={onAlternar}
        >
          <BsSearch />
        </Button>
      </div>
    </div>
  );
}

function renderGradeEdicao(
  linhas: LinhaEdicao[],
  termo: string,
  campanha: boolean,
  disabled: boolean,
  handlers: GradeEdicaoHandlers
) {
  if (linhas.length === 0) {
    return <div className="text-muted">Nenhum item na versão atual.</div>;
  }
  const visiveis = linhasFiltradas(linhas, termo, handlers.alterados);
  if (visiveis.length === 0) {
    return <div className="text-muted">Nenhum item encontrado.</div>;
  }
  return (
    <GradeEdicaoStyled style={{ maxHeight: ALTURA_MAX_ITENS }}>
      <Table striped bordered size="sm" data-testid="grade-edicao">
        <thead>
          <tr>
            {cabecalhosColunasItem(campanha)}
            <th className="acao-edicao-col">Ação</th>
            <th className="nova-qtd-edicao-col">Nova qtd.</th>
          </tr>
        </thead>
        <tbody>
          {visiveis.map(({ linha, indice }) => renderLinhaEdicao(linha, indice, campanha, disabled, handlers))}
        </tbody>
      </Table>
    </GradeEdicaoStyled>
  );
}

function renderLinhaEdicao(
  linha: LinhaEdicao,
  indice: number,
  campanha: boolean,
  disabled: boolean,
  handlers: GradeEdicaoHandlers
) {
  return (
    <tr key={`edit-${linha.idItem}-${indice}`} className={handlers.alterados.has(linha.idItem) ? 'item-editado' : undefined}>
      {celulasColunasItem(itemDaLinha(linha), campanha)}
      <td className="acao-edicao-col">
        <Form.Select
          aria-label={`acao-${indice}`}
          value={linha.acao}
          disabled={disabled}
          onChange={(e) => handlers.alterarAcao(indice, e.target.value as AcaoItemEdicao)}
        >
          <option value="MANTER">Manter</option>
          <option value="ALTERAR">Alterar quantidade</option>
          <option value="REMOVER">Remover</option>
        </Form.Select>
      </td>
      <td className="nova-qtd-edicao-col">
        <Form.Control
          type="number"
          min={0}
          aria-label={`quantidade-${indice}`}
          value={linha.quantidadeNova === 0 ? '' : linha.quantidadeNova}
          disabled={disabled || linha.acao !== 'ALTERAR'}
          onChange={(e) => handlers.alterarQuantidade(indice, Number(e.target.value))}
        />
      </td>
    </tr>
  );
}

type AdicaoHandlers = {
  selecionarNovoItem: (item: ItemBusca) => void;
  alterarQuantidadeNova: (quantidade: number) => void;
  adicionarItem: () => void;
  removerAdicao: (indice: number) => void;
  onBuscarItens?: (termo: string) => Promise<ItemBusca[]>;
};

function renderAdicao(
  novaAdicao: Adicao,
  adicoes: Adicao[],
  campanha: boolean,
  disabled: boolean,
  handlers: AdicaoHandlers
) {
  return (
    <div className="mt-3" data-testid="secao-adicao">
      <h6>Adicionar item</h6>
      <div className="d-flex align-items-end">
        <div className="me-2" style={{ minWidth: 180 }}>
          <Form.Label>Item</Form.Label>
          <ItemTypeahead
            value={novaAdicao.idItem}
            ariaLabel="novo-item"
            disabled={disabled}
            onBuscar={handlers.onBuscarItens}
            onSelecionar={handlers.selecionarNovoItem}
          />
        </div>
        <div className="me-2">
          <Form.Label>Quantidade</Form.Label>
          <Form.Control
            type="number"
            min={0}
            aria-label="quantidade-novo-item"
            value={novaAdicao.quantidade === 0 ? '' : novaAdicao.quantidade}
            disabled={disabled}
            onChange={(e) => handlers.alterarQuantidadeNova(Number(e.target.value))}
          />
        </div>
        <Button variant="outline-secondary" disabled={disabled} onClick={handlers.adicionarItem}>
          Adicionar
        </Button>
      </div>
      {adicoes.length > 0 && renderTabelaAdicoes(adicoes, campanha, disabled, handlers.removerAdicao)}
    </div>
  );
}

function renderTabelaAdicoes(
  adicoes: Adicao[],
  campanha: boolean,
  disabled: boolean,
  removerAdicao: (indice: number) => void
) {
  return (
    <TableHeaderStyled className="mt-2" style={{ maxHeight: ALTURA_MAX_ITENS }}>
      <Table striped bordered size="sm" data-testid="tabela-adicoes">
        <thead>
          <tr>
            {cabecalhosColunasItem(campanha)}
            <th>Ação</th>
          </tr>
        </thead>
        <tbody>
          {adicoes.map((a, indice) => (
            <tr key={`add-${a.idItem}-${indice}`}>
              {celulasColunasItem(itemDaAdicao(a), campanha)}
              <td>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-danger"
                  aria-label={`remover-adicao-${indice}`}
                  disabled={disabled}
                  onClick={() => removerAdicao(indice)}
                >
                  Remover
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableHeaderStyled>
  );
}

function renderAuditoria(auditoria?: LogAuditoriaPedido[]) {
  if (!auditoria || auditoria.length === 0) {
    return null;
  }
  return (
    <div className="mt-4" data-testid="secao-auditoria">
      <h6>Log de Decisões</h6>
      <TableHeaderStyled style={{ maxHeight: ALTURA_MAX_ITENS }}>
        <Table striped bordered size="sm" data-testid="tabela-auditoria">
          <thead>
            <tr>
              <th>Ação</th>
              <th>Data/Hora</th>
              <th>Usuário</th>
              <th>Descrição</th>
            </tr>
          </thead>
          <tbody>{auditoria.map(renderLinhaAuditoria)}</tbody>
        </Table>
      </TableHeaderStyled>
    </div>
  );
}

/**
 * Linha do log de decisões: ação e status humanizados na descrição, e nome do usuário no lugar
 * do id (caindo para o id quando a API não resolver).
 */
function renderLinhaAuditoria(log: LogAuditoriaPedido) {
  return (
    <tr key={log.id}>
      <td>{getAcaoLabel(log.acao)}</td>
      <td>{log.datahora ?? '-'}</td>
      <td>{log.nomeUsuario ?? log.idUsuario ?? '-'}</td>
      <td>
        <div>
          {log.justificativa && <span>{log.justificativa}</span>}
          {log.idItem && (
            <div className="text-muted small">Item: {getCodigoItem({ codigo: log.codigoItem, idItem: log.idItem })}</div>
          )}
          {log.valorAnterior && (
            <div className="text-muted small">
              De: {humanizarValorLog(log.valorAnterior)} → Para: {humanizarValorLog(log.valorNovo)}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

function renderJustificativa(valor: string, onChange: (v: string) => void, label: string) {
  return (
    <Form.Group className="mb-2 mt-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        as="textarea"
        rows={2}
        aria-label="justificativa"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
      />
    </Form.Group>
  );
}

export default FluxoAprovacaoPedidoOperadorPage;
